/**
 * @component OnboardingStepTwo
 * @description Second onboarding step for configuring movement (stand) reminders.
 * Users set work hours, movement reminder frequency, and which days to receive reminders.
 * This component also completes the onboarding process, marks user as onboarded in backend,
 * and pre-fetches critical data (settings, home data in both languages) before navigating
 * to the main dashboard.
 *
 * @flow
 * 1. Load existing movement settings from API (startTime, endTime, frequency, days, enabled)
 * 2. Allow user to modify work hours, reminder frequency, and active days
 * 3. Allow user to enable/disable movement reminders with toggle
 * 4. "Back" button navigates to exercise preferences (step 1)
 * 5. "Next" button:
 *    - Saves movement config to backend
 *    - Marks onboarding as complete
 *    - Pre-fetches settings cache (language-independent)
 *    - Pre-fetches home data in preferred language (waits for this)
 *    - Pre-fetches home data in other language in background
 *    - Navigates to /home with isFirstLogin=true flag
 */

import React, { useState, useEffect } from "react";
import {
  FormControl,
  MenuItem,
  Select,
  Container,
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Switch,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "react-toastify/dist/ReactToastify.css";
import authApiService from "../services/api/AuthApiService";
import settingsApiService from "../services/api/SettingsApiService";
import schedulerApi from "../services/api/SchedulerApiService";
import TrainingsPlanIcon from "../assets/TrainingsPlanIcon.png";
import CssBaseline from "@mui/material/CssBaseline";
import DynamicDeskLogo from "../assets/DD_2_Weiss.png";
import { useMediaQuery } from "react-responsive";
import { useAuthStore } from "../stores/zustand";
import { useSettingsQuery } from "../stores/tanstack/queries/useSettingsQuery ";
import { useQueryClient } from "@tanstack/react-query";
import { deriveCalendarEventsInScheduler } from "../hooks/useScheduler";

/**
 * OnboardingStepTwo Component
 * Manages movement reminder configuration as the final onboarding step
 */
const OnboardingStepTwo = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Movement/stand reminder configuration state
  const [startTime, setStartTime] = useState("09:00"); // Work hours start (HH:MM format)
  const [endTime, setEndTime] = useState("17:00"); // Work hours end (HH:MM format)
  const [selectedDays, setSelectedDays] = useState([]); // Array of day numbers (1-7: Mon-Sun)
  const [frequency, setFrequency] = useState("1"); // Reminder frequency in hours (0.5-4)
  const [reminderActivated, setReminderActivated] = useState(false); // Enable/disable toggle

  const [isLoading, setIsLoading] = useState(false); // Loading state during submission
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Success notification

  const isMobile = useMediaQuery({ maxWidth: 767 }); // Responsive design detection
  const { preferredLanguage } = useAuthStore(); // User's language preference (en/de)
  const email = useAuthStore((state) => state.user.email); // Current user email
  const queryClient = useQueryClient(); // TanStack Query client for cache management
  const { data: settingsData, isLoading: settingsLoading } = useSettingsQuery(); // Fetch current movement settings

  /**
   * Initialize form with existing movement settings from API
   * Converts decimal time format (from API) to HH:MM format (for UI)
   */
  useEffect(() => {
    if (settingsData?.standConfig) {
      const { standConfig } = settingsData;
      setStartTime(
        settingsApiService.decimalToTimeString(standConfig.startTime),
      );
      setEndTime(settingsApiService.decimalToTimeString(standConfig.endTime));
      setFrequency(standConfig.frequency?.toString() || "1");
      setSelectedDays(standConfig.daysOfWeek || []);
      setReminderActivated(standConfig.isEnabled || false);
    }
  }, [settingsData]);

  /**
   * Toggle movement reminder enabled/disabled
   */
  const handleReminderToggle = (event) => {
    setReminderActivated(event.target.checked);
  };

  /**
   * Handle day checkbox selection/deselection
   * Updates selectedDays array by toggling day number (1-7)
   */
  const handleDaySelection = (event) => {
    const selected = parseInt(event.target.name, 10);
    let updatedSelectedDays;

    if (selectedDays.includes(selected)) {
      updatedSelectedDays = selectedDays.filter((day) => day !== selected);
    } else {
      updatedSelectedDays = [...selectedDays, selected];
    }

    setSelectedDays(updatedSelectedDays);
  };

  /**
   * Navigate back to exercise preferences (step 1)
   * Passes email through location state to preserve context
   */
  const navigateBackToExercise = (event) => {
    const emailFromState = location.state?.email;
    navigate("/onboarding-exercises", { state: { email: emailFromState } });
  };

  /**
   * Prepare and send movement config to backend API
   * Converts HH:MM format (UI) to decimal format (API expected format)
   * Includes timezone offset for server-side scheduling
   */
  async function updateConfig() {
    const config = {
      startTime: settingsApiService.timeStringToDecimal(startTime),
      endTime: settingsApiService.timeStringToDecimal(endTime),
      frequency: parseFloat(frequency),
      daysOfWeek: selectedDays,
      isEnabled: reminderActivated,
      offset: settingsApiService.getFormattedTimezoneOffset(),
    };

    try {
      await settingsApiService.updateMovementConfig(config);
    } catch (error) {
      console.error("Error updating movement config:", error);
      throw error;
    }
  }

  /**
   * Complete onboarding flow:
   * 1. Save movement configuration to backend
   * 2. Mark user as onboarded (onboarding complete flag)
   * 3. Pre-fetch and cache settings data (language-independent)
   * 4. Pre-fetch and cache home data in current language (wait for completion)
   * 5. Pre-fetch home data in other language in background (don't wait)
   * 6. Navigate to /home with isFirstLogin flag for first-login experience
   *
   * NOTE: Pre-fetching both language versions ensures smooth language switching
   * and populates caches before user accesses main dashboard
   */
  const saveAndNavigateToHome = async (event) => {
    try {
      setIsLoading(true);

      // Save movement config (stand reminder settings)
      await updateConfig();

      // Mark onboarding as complete in backend
      await authApiService.completeOnboarding();

      // Calculate timezone offset for API queries
      const offset = new Date().getTimezoneOffset() / -60;
      const timezoneOffset =
        offset >= 0
          ? `+${String(Math.abs(offset)).padStart(2, "0")}`
          : `-${String(Math.abs(offset)).padStart(2, "0")}`;

      // Pre-fetch settings data (language-independent cache) - WAIT for completion
      await queryClient.fetchQuery({
        queryKey: ["settings", email],
        queryFn: async () => {
          const response = await settingsApiService.getCompleteSettings();
          return {
            standConfig: response.standConfig || {
              startTime: 9.0,
              endTime: 17.0,
              frequency: 1.0,
              daysOfWeek: [],
              isEnabled: false,
              offset: null,
            },
            excerciseConfig: response.excerciseConfig || {
              startTime: 9.0,
              endTime: 17.0,
              frequency: 1.0,
              daysOfWeek: [],
              isEnabled: false,
              isPhysioEnabled: false,
              preferences: [],
            },
          };
        },
        staleTime: 5 * 60 * 1000,
      });

      const videosData = queryClient.getQueryData([
        "videos",
        email,
        preferredLanguage,
      ]) || { allVideos: [], physioVideos: [] };

      // Pre-fetch home data in current language - WAIT for completion
      // This populates the calendar and notifications before navigation
      await queryClient.fetchQuery({
        queryKey: ["homeData", email, timezoneOffset, preferredLanguage],
        queryFn: async () => {
          const response = await schedulerApi.getHomeData(
            timezoneOffset,
            preferredLanguage,
          );
          const calendarEvents = deriveCalendarEventsInScheduler(
            response.notifications || [],
            videosData,
          );
          const allSchedules =
            response.notifications?.flatMap(
              (notification) => notification.schedules || [],
            ) || [];

          return {
            notifications: response.notifications || [],
            schedules: allSchedules,
            calendarEvents: calendarEvents,
            rawHomeData: response,
          };
        },
        staleTime: 5 * 60 * 1000,
      });

      // Pre-fetch home data in other language in BACKGROUND - don't wait for this
      // Ensures smooth language switching without additional API calls later
      const otherLanguage = preferredLanguage === "en" ? "de" : "en";
      setTimeout(async () => {
        try {
          const otherVideosData = queryClient.getQueryData([
            "videos",
            email,
            otherLanguage,
          ]) || { allVideos: [], physioVideos: [] };

          await queryClient.fetchQuery({
            queryKey: ["homeData", email, timezoneOffset, otherLanguage],
            queryFn: async () => {
              const response = await schedulerApi.getHomeData(
                timezoneOffset,
                otherLanguage,
              );
              const calendarEvents = deriveCalendarEventsInScheduler(
                response.notifications || [],
                otherVideosData,
              );
              const allSchedules =
                response.notifications?.flatMap(
                  (notification) => notification.schedules || [],
                ) || [];
              return {
                notifications: response.notifications || [],
                schedules: allSchedules,
                calendarEvents: calendarEvents,
                rawHomeData: response,
              };
            },
            staleTime: 5 * 60 * 1000,
          });
        } catch (error) {
          console.error(
            `[OnboardingStepTwo] Background fetch failed for ${otherLanguage}:`,
            error,
          );
        }
      }, 1500);

      setIsLoading(false);
      setSnackbarOpen(true);

      // Navigate to home with first-login flag for welcome experience
      var isFirstLogin = true;
      navigate("/home", { state: { isFirstLogin } });
    } catch (error) {
      console.error("[OnboardingStepTwo] Error completing onboarding:", error);
      setIsLoading(false);
      // Optionally show error snackbar
    }
  };

  /**
   * Close success notification snackbar
   */
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Two-column layout: welcome message (left), form (right)
  return (
    <Container component="main" maxWidth="lg" sx={{ height: "100vh" }}>
      <CssBaseline />
      {/* Logo positioned at top-left corner */}
      <Typography
        variant="h5"
        sx={{
          position: "absolute",
          top: 16,
          left: 24,
          color: "#fff",
        }}
      >
        <img
          src={DynamicDeskLogo}
          style={{
            width: isMobile ? "24%" : "12%",
            height: isMobile ? "24%" : "12%",
            paddingTop: "12px",
          }}
          alt="DynamicDeskLogo"
        />
      </Typography>
      {/* Main container with two-column layout */}
      <Grid container sx={{ height: "100%" }}>
        {/* Left column: Welcome message */}
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 8,
            overflowY: "auto",
          }}
        >
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ color: "#fff" }}>
            <b>{t("keepMoving")}</b>
          </Typography>
          <Typography variant="h6" sx={{ color: "#fff", marginTop: "36px" }}>
            <b>{t("activateNeverMiss")}</b>
          </Typography>
        </Grid>
        {/* Right column: Onboarding form */}
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 1,
          }}
        >
          <div
            style={{ width: "100vh", padding: "36px" }}
            className={
              isMobile ? "white-background-mobile" : "white-background"
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <img
                src={TrainingsPlanIcon}
                style={{ width: "124px", height: "124px", alignSelf: "end" }}
                alt="TrainingsPlanIcon"
              />
              {/* Form title */}
              <Typography
                component="h1"
                variant="h5"
                style={{ color: "#063bd0", alignSelf: "flex-start" }}
              >
                <b>{t("stepTwoMovement")}</b>
              </Typography>
              <Typography
                component="h1"
                style={{ color: "#063bd0", alignSelf: "flex-start" }}
              >
                <b>{t("howOftenMovement")}</b>
              </Typography>
              {/* Form grid containing all input fields */}
              <div style={{ marginTop: "36px" }}>
                <Grid container spacing={2}>
                  {/* Start time input - earliest time user wants to receive movement reminders */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t("startTime")}
                      type="time"
                      value={startTime}
                      onChange={(e) => {
                        setStartTime(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  {/* End time input - latest time user wants to receive movement reminders */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t("endTime")}
                      type="time"
                      value={endTime}
                      onChange={(e) => {
                        setEndTime(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  {/* Movement frequency dropdown - interval between stand reminders (0.5-4 hours) */}
                  <Grid item xs={12} sm={4}>
                    <FormControl>
                      <Select
                        value={frequency}
                        onChange={(e) => {
                          setFrequency(e.target.value);
                        }}
                      >
                        <MenuItem value="0.5">{t("every30mins")}</MenuItem>
                        <MenuItem value="1">{t("every60mins")}</MenuItem>
                        <MenuItem value="1.5">{t("every90mins")}</MenuItem>
                        <MenuItem value="2">{t("every120mins")}</MenuItem>
                        <MenuItem value="2.5">{t("every150mins")}</MenuItem>
                        <MenuItem value="3">{t("every180mins")}</MenuItem>
                        <MenuItem value="3.5">{t("every210mins")}</MenuItem>
                        <MenuItem value="4">{t("every240mins")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {/* Day selection checkboxes - allows user to choose which days to receive movement reminders */}
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <Grid container>
                        <Grid
                          item
                          xs={12}
                          container
                          justifyContent="flex-start"
                        >
                          <FormGroup row>
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(1)}
                                  onChange={handleDaySelection}
                                  name="1"
                                />
                              }
                              label={t("monday")}
                            />
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(2)}
                                  onChange={handleDaySelection}
                                  name="2"
                                />
                              }
                              label={t("tuesday")}
                            />
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(3)}
                                  onChange={handleDaySelection}
                                  name="3"
                                />
                              }
                              label={t("wednesday")}
                            />
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(4)}
                                  onChange={handleDaySelection}
                                  name="4"
                                />
                              }
                              label={t("thursday")}
                            />
                          </FormGroup>
                        </Grid>
                        <Grid item xs={12}>
                          <FormGroup row>
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(5)}
                                  onChange={handleDaySelection}
                                  name="5"
                                />
                              }
                              label={t("friday")}
                            />
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(6)}
                                  onChange={handleDaySelection}
                                  name="6"
                                />
                              }
                              label={t("saturday")}
                            />
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(7)}
                                  onChange={handleDaySelection}
                                  name="7"
                                />
                              }
                              label={t("sunday")}
                            />
                          </FormGroup>
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>
                </Grid>
                <div style={{ paddingLeft: "24px" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={reminderActivated}
                        onChange={handleReminderToggle}
                        name="reminderActivated"
                      />
                    }
                    label={t("reminderActivated")}
                    sx={{ mt: 2, mb: 1, alignSelf: "flex-start" }}
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    gutterBottom
                    sx={{ alignSelf: "flex-start", mb: 2 }}
                  >
                    {t("switchHere")}
                  </Typography>
                </div>
                <Button
                  onClick={navigateBackToExercise}
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    float: "left",
                    width: "25%",
                    marginTop: "36px",
                  }}
                >
                  {t("back")}
                </Button>
                <Button
                  onClick={saveAndNavigateToHome}
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    float: "right",
                    width: "25%",
                    marginTop: "36px",
                  }}
                >
                  {t("next")}
                </Button>
              </div>
            </Box>
          </div>
        </Grid>
      </Grid>

      {/* Loading dialog */}
      <Dialog
        open={isLoading || settingsLoading}
        PaperProps={{
          style: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          },
        }}
      >
        <CircularProgress />
        <Typography style={{ marginLeft: "10px" }}>{t("loading")}</Typography>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          {t("settingsSavedSuccessfully")}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OnboardingStepTwo;
